<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isContentLoading">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else-if="!isContentLoading && orderedPrograms.length > 0 && orderedPlans.length > 0">
      <div v-for="program in orderedPrograms" :key="program.businessProgramId" class="tw-flex tw-items-center tw-mb-6">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex-col">
            <base-checkbox :value="program.businessProgramId" v-model="selectedPrograms" :disabled="isPscSecondaryRole">
              <div class="tw-flex-col tw-my-auto">
                {{ program.name }}
                <div class="tw-text-gray-500 tw-text-xs tw-font-normal tw-mt-1">{{ program.programGroup }}</div>
              </div>
            </base-checkbox>
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-select
          class="tw-flex-grow"
          :options="getFilteredPlans(program.name)"
          label-key="name"
          value-key="businessPlanId"
          placeholder="Select a plan"
          :value="mappings[program.businessProgramId]"
          :disabled="isPscSecondaryRole"
          @input="handleInput(program.businessProgramId, $event)"
        />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between" v-if="!isPscSecondaryRole">
        <base-button type="flat" @click="selectAll">{{ areAllSelected ? "Deselect All" : "Select All" }}</base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()">
            Cancel
          </base-button>
          <base-button :loading="isSaving" class="tw-w-auto" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";
import { mapGetters } from "vuex";

export default {
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isContentLoading: false,
      isSaving: false,
      businessLocation: {},
      programs: [],
      plans: [],
      selectedPrograms: [],
      mappings: {},
    };
  },
  computed: {
    ...mapGetters("role", ["isPscSecondaryRole"]),
    title() {
      return `${this.businessLocation.name} Mappings`;
    },
    orderedPrograms() {
      return orderBy(this.programs, ["name", "programGroup"], ["asc", "asc"]);
    },
    orderedPlans() {
      return orderBy(this.plans, ["name"], ["asc"]);
    },
    areAllSelected() {
      return this.programs.every((l) => !!this.selectedPrograms.find((sl) => sl === l.businessProgramId));
    },
  },
  created() {
    this.loadAllInitialData();
  },
  methods: {
    async loadAllInitialData() {
      this.isContentLoading = true;
      try {
        await Promise.all([this.getPrograms(), this.getPlans()]);
      } catch (error) {
        console.error("Failed to load initial data:", error);
      }
    },
    show(businessLocation) {
      this.businessLocation = businessLocation;
      this.$refs.slideover.show();
      this.selectedPrograms = [];
      this.getMappingsForLocation();
    },
    hide() {
      this.$refs.slideover.hide();
      this.selectedPrograms = [];
      this.mappings = {};
      this.businessLocation = {};
      this.isContentLoading = false;
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);
        this.programs = response.data;
      } catch (error) {
        console.error("Error fetching programs:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to load programs",
          message: `There was an error fetching the list of programs.`,
          error,
        });
        throw error;
      }
    },
    async getPlans() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } catch (error) {
        console.error("Error fetching plans:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to load plans",
          message: `There was an error fetching the list of plans.`,
          error,
        });
        throw error; // Re-throw to be caught by loadAllInitialData or getMappingsForLocation
      }
    },
    async getMappingsForLocation() {
      if (!this.businessLocation.businessLocationId) {
        console.warn("Cannot fetch mappings: businessLocation.businessLocationId is missing.");
        this.isContentLoading = false; // Turn off loader if no valid ID
        return;
      }

      this.isContentLoading = true; // Turn on loader
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`);
        this.mappings = {};
        response.data.forEach((m) => {
          this.mappings[m.businessProgramId] = m.businessPlanId;
        });
        this.$forceUpdate();
        // Set isContentLoading to false ONLY after successful data processing
        this.isContentLoading = false;
      } catch (error) {
        console.error("Error fetching mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to load mappings",
          message: `We were not able to load the mappings for this location.`,
          error,
        });
        // Set isContentLoading to false on error as well
        this.isContentLoading = false;
        throw error; // Re-throw to propagate the error if other parts of the app need to handle it
      }
    },
    async save() {
      try {
        this.isSaving = true;
        await this.$axios.post(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`, {
          mappings: this.mappings,
        });
        this.hide();
        this.selectedPrograms = [];
        this.mappings = {};
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save mappings",
          message: `We were not able to save the mappings for this location. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(businessProgramId, businessPlanId) {
      if (this.selectedPrograms.includes(businessProgramId)) {
        this.selectedPrograms.forEach((programId) => {
          this.mappings[programId] = businessPlanId;
        });
      } else {
        this.mappings[businessProgramId] = businessPlanId;
      }
      this.$forceUpdate();
    },
    selectAll() {
      if (this.areAllSelected) {
        this.selectedPrograms = [];
      } else {
        this.selectedPrograms = this.programs.map((l) => l.businessProgramId);
      }
    },

    extractCoreProgramName(programFullName) {
      if (!programFullName) {
        return "";
      }
      let coreName = programFullName.split("(")[0].trim();

      coreName = coreName.replace(/\s*\b(SL Tablet|Injection|Stand-Alone|Add-On|Nasal Spray|Starter Kit|TEST|SLIT|Testing|Kit)\b$/i, "").trim();

      coreName = coreName.replace(/\s*[-_]?\s*\b(TEST|SLIT|Injection|Kit|Testing)\b$/i, "").trim();

      coreName = coreName.replace(/^cmwl\s*\|\s*/i, "").trim();

      return coreName;
    },

    extractParentheticalPart(name) {
      const match = name.match(/\((.*?)\)/);
      return match ? match[1].toLowerCase().trim() : null;
    },

    extractProgramTypeSuffix(programFullName) {
      const match = programFullName.match(/\b(TEST|SLIT|INJECTION|TABLET)\b/i); // Add more as needed
      return match ? match[1].toLowerCase() : null;
    },

    getCleanedPlanNameForMatching(planFullName) {
      if (!planFullName) {
        return "";
      }
      let cleanedName = planFullName;

      cleanedName = cleanedName
        .replace(/\s*(?:\|\s*\$\d+(?:\.\d{2})?|\$\d+(?:\.\d{2})?)\s*(- Dev Plan)?(?:\s*\|\s*(?:Starter|Maintenance|On-Going|Test Plan|Pre-Rollout|Phase\s*\d))?$/gi, "")
        .trim();

      cleanedName = cleanedName.replace(/\s*\|\s*(Starter|Maintenance|On-Going|Test Plan|Pre-Rollout|Phase\s*\d)\s*$/gi, "").trim();

      cleanedName = cleanedName.replace(/^cmwl\s*\|\s*/i, "").trim();

      return cleanedName;
    },

    getFilteredPlans(programName) {
      if (!this.orderedPlans || this.orderedPlans.length === 0) {
        return [];
      }

      const cleanProgramNameLower = programName.toLowerCase();
      const coreProgramName = this.extractCoreProgramName(programName).toLowerCase(); // e.g., "alleviate"
      const programTypeSuffix = this.extractProgramTypeSuffix(programName); // e.g., "test"
      const programParentheticalPart = this.extractParentheticalPart(programName); // e.g., "spiriplex 40 ige testing kit"

      return this.orderedPlans.filter((plan) => {
        const cleanPlanName = this.getCleanedPlanNameForMatching(plan.name).toLowerCase();

        // Rule 0: Specific rule for Hawaii patients
        if (cleanProgramNameLower.startsWith("hawaii patients")) {
          return cleanPlanName.startsWith("hawaii");
        }

        if (programTypeSuffix) {
          const expectedPlanNameWithSuffix = `${coreProgramName} ${programTypeSuffix}`;
          if (cleanPlanName === expectedPlanNameWithSuffix.trim()) {
            return true;
          }
          if (cleanPlanName === coreProgramName) {
            const originalPlanNameLower = plan.name.toLowerCase();
            const suffixRegex = new RegExp(`\\b${programTypeSuffix}\\b`, "i");
            if (suffixRegex.test(originalPlanNameLower)) {
              return true;
            }
          }
        } else if (cleanPlanName === coreProgramName) {
          // If no specific suffix in programName (e.g., "Get Fit"), then an exact match to core is good.
          return true;
        }
        if (coreProgramName && cleanPlanName.startsWith(coreProgramName)) {
          if (programTypeSuffix) {
            const suffixRegex = new RegExp(`\\b${programTypeSuffix}\\b`, "i");
            if (suffixRegex.test(cleanPlanName)) {
              return true;
            }
          } else {
            // If program has no specific type suffix (e.g., "Get Fit"), a startsWith match is strong.
            return true;
          }
        }
        const programNameWithoutParenAndSuffix = programName
          .split("(")[0]
          .toLowerCase()
          .trim();
        let strippedProgramName = programNameWithoutParenAndSuffix
          .replace(/\s*\b(SL Tablet|Injection|Stand-Alone|Add-On|Nasal Spray|Starter Kit|TEST|SLIT|Testing|Kit)\b$/i, "")
          .trim();
        strippedProgramName = strippedProgramName.replace(/\s*[-_]?\s*\b(TEST|SLIT|Injection|Kit|Testing)\b$/i, "").trim();

        if (cleanPlanName === strippedProgramName && strippedProgramName.length > 0) {
          return true;
        }
        // General inclusion, but with strict type checks to prevent "SLIT" matches for "TEST" programs.
        if (cleanPlanName.includes(strippedProgramName) && strippedProgramName.length > 0) {
          const planContainsSlit = /\b(slit)\b/.test(cleanPlanName);
          const programIsSlit = programTypeSuffix === "slit";
          const planContainsTest = /\b(test)\b/.test(cleanPlanName);
          const programIsTest = programTypeSuffix === "test";

          // If the plan has "SLIT" but the program is NOT "SLIT", don't match.
          if (planContainsSlit && !programIsSlit) {
            return false;
          }
          // If the plan has "TEST" but the program is NOT "TEST", don't match.
          if (planContainsTest && !programIsTest) {
            return false;
          }

          // If it passes the type specific exclusions, then a general inclusion is allowed.
          return true;
        }

        // PRIORITY 4: Check if the program's parenthetical part is present in the plan name.
        if (programParentheticalPart && cleanPlanName.includes(programParentheticalPart)) {
          return true;
        }

        return false; // If no rule matched
      });
    },
  },
};
</script>
